import { Box } from "@mui/material";
import { MprcText } from "components";
import { MprcChip } from "components/MprcChip";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { AcceptedConstraints, GroupType, GroupUsages, PermissionConstraintDto, PermissionConstraintGroupDto, PermissionConstraintModel, PermissionConstraintUnitDto, PermissionConstraintUnitTypeResponse, RolePermissionDto } from "generated/CoreApi_v1";
import { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { GroupSelectorDrawer } from "views/foundation/group/drawers/GroupSelectorDrawer";
import { UnitSelectionDialog } from "views/foundation/unit/components/UnitSelectionDialog";
import { UnitTypeSelectionDrawer } from "views/foundation/unit/drawers/UnitTypeSelectionDrawer";

export type ConstraintTypes =
    | "global"
    | "selfUserUnitTypes"
    | "units"
    | "groups"
    | "directSubordinates"
    | "allSubordinates"
    | "selfUserOwnedObjects"
    | "selfUser";

export type ConstraintSelectionType = PermissionConstraintUnitTypeResponse | PermissionConstraintGroupDto | PermissionConstraintUnitDto | boolean | undefined;

export type DeleteConstraintType = (ct: ConstraintTypes, item?: PermissionConstraintUnitTypeResponse | PermissionConstraintGroupDto | PermissionConstraintUnitDto) => void;

export type AssignOptions = "users" | "groups";

export function getConstraintTypeNames(t: TFunction): Record<ConstraintTypes, string> {
    return {
        "global": t("Role.Edit.Helpers.GlobalText"),
        "selfUserUnitTypes": t("Role.Edit.Helpers.SelfUserUnitTypesText"),
        "units": t("SysObjects.Units", { ns: "shared" }),
        "groups": t("SysObjects.Groups", { ns: "shared" }),
        "directSubordinates": t("Role.Edit.Helpers.DirectSubordinatesText"),
        "allSubordinates": t("Role.Edit.Helpers.AllSubordinatesText"),
        "selfUserOwnedObjects": t("Role.Edit.Helpers.SelfUserOwnedObjectsText"),
        "selfUser": t("Role.Edit.Helpers.SelfUserText")
    };
}

export function selectConstraint(ct: ConstraintTypes, defaultValue: any, drawerSetter: any, t: TFunction): Promise<any> {
    return new Promise((resolve) => {

        switch (ct) {
            case "selfUserUnitTypes":
                drawerSetter(
                    <UnitTypeSelectionDrawer
                        onClose={
                            (selectedItem) => {
                                drawerSetter(null);
                                resolve(selectedItem);
                            }}
                    />
                );
                break;
            case "units":
                drawerSetter(
                    <UnitSelectionDialog
                        showDescendantOption
                        selectedItems={[]}
                        onError={e => { }}
                        onClose={() => drawerSetter(null)}
                        multiSelect={false}
                        selectedMode="free"
                        title={t("Role.Edit.Helpers.UnitDrawerTitle")}
                        onChange={
                            (selectedItem) => {
                                drawerSetter(null);
                                resolve(selectedItem ?
                                    {
                                        unitId: selectedItem[0].key,
                                        name: selectedItem[0].label,
                                        unitTypeId: selectedItem[0].unitTypeId,
                                        unitTypeName: selectedItem[0].unitTypeName,
                                        includeDescendants: selectedItem[0].includeDescendants,
                                        linkedGroupId: selectedItem[0].includeDescendants ? selectedItem[0].allUsersGroup.id : selectedItem[0].usersGroup.id
                                    }
                                    : undefined
                                );
                            }
                        }
                    />
                );
                break;
            case "groups":
                drawerSetter(
                    <GroupSelectorDrawer
                        title={t("Role.Edit.Helpers.GroupDrawerTitle")}
                        groupTypes={[
                            GroupType.Static, GroupType.Dynamic,
                            GroupType.AllUsers, GroupType.ExternalUsers, GroupType.InternalUsers,
                            GroupType.AllSubordinates, GroupType.DirectSubordinates,
                        ]}
                        groupUsages={GroupUsages.Administration}
                        onClose={
                            (selectedItem) => {
                                drawerSetter(null);
                                resolve(selectedItem != undefined && selectedItem.length > 0 ? selectedItem[0] : undefined);
                            }
                        }
                    />
                );
                break;
            default:
                resolve(defaultValue);
        }
    });
}

export function constraintsAreEquals(c1: PermissionConstraintDto, c2: PermissionConstraintDto): boolean {
    return c1.allSubordinates === c2.allSubordinates
        && c1.directSubordinates === c2.directSubordinates
        && c1.selfUser === c2.selfUser
        && c1.selfUserOwnedObjects === c2.selfUserOwnedObjects
        && (c1.groups || []).filter((v) => (c2.groups || []).findIndex(x => x.groupId == v.groupId) == -1).length == 0
        && (c2.groups || []).filter((v) => (c1.groups || []).findIndex(x => x.groupId == v.groupId) == -1).length == 0
        && (c1.selfUserUnitTypes || []).filter((v) => (c2.selfUserUnitTypes || []).findIndex(x => x.unitTypeId == v.unitTypeId) == -1).length == 0
        && (c2.selfUserUnitTypes || []).filter((v) => (c1.selfUserUnitTypes || []).findIndex(x => x.unitTypeId == v.unitTypeId) == -1).length == 0
        && (c1.units || []).filter((v) => (c2.units || []).findIndex(x => x.unitId == v.unitId) == -1).length == 0
        && (c2.units || []).filter((v) => (c1.units || []).findIndex(x => x.unitId == v.unitId) == -1).length == 0
        ;
}

export function convertToPermissionConstraintModel(v: PermissionConstraintDto): PermissionConstraintModel {
    return {
        global: v.global,
        allSubordinates: v.allSubordinates,
        directSubordinates: v.directSubordinates,
        selfUser: v.selfUser,
        selfUserOwnedObjects: v.selfUserOwnedObjects,
        groups: v.groups == null ? null : v.groups.map((v) => v.groupId),
        selfUserUnitTypes: v.selfUserUnitTypes == null ? null : v.selfUserUnitTypes.map((v) => v.unitTypeId),
        units: v.units == null ? null : v.units.map((v) => ({
            unitId: v.unitId,
            includeDescendants: v.includeDescendants,
            linkedGroupId: v.linkedGroupId,
            unitTypeId: v.unitTypeId,
            unitTypeName: v.unitTypeName
        }))
    };
}

export function acceptedContraintsToConstraintTypes(acceptedConstraints: AcceptedConstraints) {
    const result: ConstraintTypes[] = [];
    if ((acceptedConstraints & AcceptedConstraints.SelfUserUnitType) > 0)
        result.push("selfUserUnitTypes");
    if ((acceptedConstraints & AcceptedConstraints.Units) > 0)
        result.push("units");
    if ((acceptedConstraints & AcceptedConstraints.Groups) > 0)
        result.push("groups");
    if ((acceptedConstraints & AcceptedConstraints.SelfUserSubordinates) > 0) {
        result.push("directSubordinates");
        result.push("allSubordinates");
    }
    if ((acceptedConstraints & AcceptedConstraints.SelfUserOwnedObjects) > 0)
        result.push("selfUserOwnedObjects");
    if ((acceptedConstraints & AcceptedConstraints.SelfUser) > 0)
        result.push("selfUser");

    return result;
}

export function isGlobalOnlyConstraint(ct: PermissionConstraintDto): boolean {
    return ct.global === true
        && !ct.allSubordinates
        && !ct.directSubordinates
        && (ct.groups || []).length == 0
        && !ct.selfUser
        && !ct.selfUserOwnedObjects
        && (ct.selfUserUnitTypes || []).length == 0
        && (ct.units || []).length == 0
        ;
}

export function isEmptyConstraint(ct: PermissionConstraintDto): boolean {
    return !ct.allSubordinates
        && !ct.directSubordinates
        && (ct.groups || []).length == 0
        && !ct.selfUser
        && !ct.selfUserOwnedObjects
        && (ct.selfUserUnitTypes || []).length == 0
        && (ct.units || []).length == 0
        ;
}

export function buildInitialContraints(acceptedConstraints: AcceptedConstraints): PermissionConstraintDto {
    const result: PermissionConstraintDto = {
        global: (acceptedConstraints & AcceptedConstraints.Global) > 0,
        allSubordinates: (acceptedConstraints & AcceptedConstraints.SelfUserSubordinates) > 0 ? false : null,
        directSubordinates: (acceptedConstraints & AcceptedConstraints.SelfUserSubordinates) > 0 ? false : null,
        selfUser: (acceptedConstraints & AcceptedConstraints.SelfUser) > 0 ? false : null,
        selfUserOwnedObjects: (acceptedConstraints & AcceptedConstraints.SelfUserOwnedObjects) > 0 ? false : null,
        groups: (acceptedConstraints & AcceptedConstraints.Groups) > 0 ? [] : null,
        selfUserUnitTypes: (acceptedConstraints & AcceptedConstraints.SelfUserUnitType) > 0 ? [] : null,
        units: (acceptedConstraints & AcceptedConstraints.Units) > 0 ? [] : null
    };

    return result;
}


export function getPermissionConstraintMenuItems(acceptedConstraints: AcceptedConstraints, constraints: PermissionConstraintDto, t: TFunction): MprcDropdownItems<ConstraintTypes> {
    const names = getConstraintTypeNames(t);

    const group0: MprcDropdownItems<ConstraintTypes> = [];
    if ((acceptedConstraints & AcceptedConstraints.SelfUserUnitType) > 0)
        group0.push({ text: names.selfUserUnitTypes + "...", value: "selfUserUnitTypes" });
    if ((acceptedConstraints & AcceptedConstraints.Units) > 0)
        group0.push({ text: names.units + "...", value: "units" });
    if ((acceptedConstraints & AcceptedConstraints.Groups) > 0)
        group0.push({ text: names.groups + "...", value: "groups" });

    const group1: MprcDropdownItems<ConstraintTypes> = [];
    if ((acceptedConstraints & AcceptedConstraints.SelfUserSubordinates) > 0) {
        if (constraints.directSubordinates !== true)
            group1.push({ text: names.directSubordinates, value: "directSubordinates" });
        if (constraints.allSubordinates !== true)
            group1.push({ text: names.allSubordinates, value: "allSubordinates" });
    }

    const group2: MprcDropdownItems<ConstraintTypes> = [];
    if ((acceptedConstraints & AcceptedConstraints.SelfUserOwnedObjects) > 0 && constraints.selfUserOwnedObjects !== true)
        group2.push({ text: names.selfUserOwnedObjects, value: "selfUserOwnedObjects" });
    if ((acceptedConstraints & AcceptedConstraints.SelfUser) > 0 && constraints.selfUser !== true)
        group2.push({ text: names.selfUser, value: "selfUser" });

    return MprcDropdownBuildGroupedItems([group0, group1, group2]);
}

export function getDefaultConstraintMenuItems(t: TFunction, currentContraints?: PermissionConstraintDto): MprcDropdownItems<ConstraintTypes> {
    const names = getConstraintTypeNames(t);

    const group0: MprcDropdownItems<ConstraintTypes> = [
        { text: names.selfUserUnitTypes + "...", value: "selfUserUnitTypes" },
        { text: names.units + "...", value: "units" },
        { text: names.groups + "...", value: "groups" }
    ];

    const group1: MprcDropdownItems<ConstraintTypes> = [];
    if (currentContraints === undefined || currentContraints.directSubordinates !== true)
        group1.push({ text: names.directSubordinates, value: "directSubordinates" });
    if (currentContraints === undefined || currentContraints.allSubordinates !== true)
        group1.push({ text: names.allSubordinates, value: "allSubordinates" });

    const group2: MprcDropdownItems<ConstraintTypes> = [];
    if (currentContraints === undefined || currentContraints.selfUserOwnedObjects !== true)
        group2.push({ text: names.selfUserOwnedObjects, value: "selfUserOwnedObjects" });
    if (currentContraints === undefined || currentContraints.selfUser !== true)
        group2.push({ text: names.selfUser, value: "selfUser" });

    return MprcDropdownBuildGroupedItems([group0, group1, group2]);
}

export function copyConstraints(permission: RolePermissionDto, defaultContraint: PermissionConstraintDto) {
    if (permission.acceptedConstraints == AcceptedConstraints.Global) {
        permission.constraints.global = true;
    }
    else {
        if (defaultContraint.groups != null)
            defaultContraint.groups.forEach((item) => mergeGroupConstraint(permission, item, "add"));
        if (defaultContraint.units != null)
            defaultContraint.units.forEach((item) => mergeUnitConstraint(permission, item, "add"));
        if (defaultContraint.selfUserUnitTypes != null)
            defaultContraint.selfUserUnitTypes.forEach((item) => mergeUnitTypeConstraint(permission, item, "add"));
        mergeConstraints(permission, defaultContraint);
    }
}

export function mergeConstraints(permission: RolePermissionDto, defaultConstraint: PermissionConstraintDto) {

    if (permission.acceptedConstraints != AcceptedConstraints.Global) {

        if ((permission.acceptedConstraints & AcceptedConstraints.SelfUser) > 0 && defaultConstraint.selfUser != null) {
            permission.constraints.selfUser = defaultConstraint.selfUser;
        }

        if ((permission.acceptedConstraints & AcceptedConstraints.SelfUserOwnedObjects) > 0 && defaultConstraint.selfUserOwnedObjects != null) {
            permission.constraints.selfUserOwnedObjects = defaultConstraint.selfUserOwnedObjects;
        }

        if ((permission.acceptedConstraints & AcceptedConstraints.SelfUserSubordinates) > 0) {
            if (defaultConstraint.allSubordinates != null)
                permission.constraints.allSubordinates = defaultConstraint.allSubordinates;

            if (defaultConstraint.directSubordinates != null)
                permission.constraints.directSubordinates = defaultConstraint.directSubordinates;
        }

        if ((permission.acceptedConstraints & AcceptedConstraints.Global) > 0)
            permission.constraints.global = isEmptyConstraint(permission.constraints);
        else
            permission.constraints.global = false;
    }
}

export function mergeGroupConstraint(permission: RolePermissionDto, selection: PermissionConstraintGroupDto, operation: string) {
    if ((permission.acceptedConstraints & AcceptedConstraints.Groups) > 0 && permission.constraints.groups != null) {
        if (operation == "add") {
            if (permission.constraints.groups.find(p => p.groupId == selection.groupId) == null)
                permission.constraints.groups.push(selection);
        }
        else {
            permission.constraints.groups = permission.constraints.groups.filter(p => p.groupId != selection.groupId);
        }

        if ((permission.acceptedConstraints & AcceptedConstraints.Global) > 0) {
            permission.constraints.global = isEmptyConstraint(permission.constraints);
        }
    }
}

export function mergeUnitConstraint(permission: RolePermissionDto, selection: PermissionConstraintUnitDto, operation: string) {
    if ((permission.acceptedConstraints & AcceptedConstraints.Units) > 0 && permission.constraints.units != null) {
        if (operation == "add") {
            if (permission.constraints.units.find(p => p.unitId == selection.unitId) == null)
                permission.constraints.units.push(selection);
        }
        else {
            permission.constraints.units = permission.constraints.units!.filter(p => p.unitId != selection.unitId);
        }

        if ((permission.acceptedConstraints & AcceptedConstraints.Global) > 0) {
            permission.constraints.global = isEmptyConstraint(permission.constraints);
        }
    }
}

export function mergeUnitTypeConstraint(permission: RolePermissionDto, selection: PermissionConstraintUnitTypeResponse, operation: string) {
    if ((permission.acceptedConstraints & AcceptedConstraints.SelfUserUnitType) > 0 && permission.constraints.selfUserUnitTypes != null) {
        if (operation == "add") {
            if (permission.constraints.selfUserUnitTypes.find(p => p.unitTypeId == selection.unitTypeId) == null)
                permission.constraints.selfUserUnitTypes.push(selection);
        }
        else {
            permission.constraints.selfUserUnitTypes = permission.constraints.selfUserUnitTypes.filter(p => p.unitTypeId != selection.unitTypeId);
        }
        if ((permission.acceptedConstraints & AcceptedConstraints.Global) > 0) {
            permission.constraints.global = isEmptyConstraint(permission.constraints);
        }
    }
}

export function addConstraint(
    target: PermissionConstraintDto,
    ct: ConstraintTypes,
    selection: ConstraintSelectionType,
    permissions?: RolePermissionDto[]) {

    if (selection) {
        const ut = selection as PermissionConstraintUnitTypeResponse;
        const unit = selection as PermissionConstraintUnitDto;
        const gp = selection as PermissionConstraintGroupDto;
        switch (ct) {
            case "selfUserUnitTypes":
                if (target.selfUserUnitTypes == null) target.selfUserUnitTypes = [];
                if (target.selfUserUnitTypes.find(p => p.unitTypeId == ut.unitTypeId) != null) return false;
                target.selfUserUnitTypes.push({ unitTypeId: ut.unitTypeId, name: ut.name });
                target.global = false;
                permissions?.forEach(p => mergeUnitTypeConstraint(p, ut, "add"));
                break;
            case "units":
                if (target.units == null) target.units = [];
                if (target.units.find(p => p.unitId == unit.unitId) != null) return false;
                target.units.push({
                    unitId: unit.unitId,
                    name: unit.name,
                    unitTypeId: unit.unitTypeId,
                    unitTypeName: unit.unitTypeName,
                    includeDescendants: unit.includeDescendants,
                    linkedGroupId: unit.linkedGroupId
                });
                target.global = false;
                permissions?.forEach(p => mergeUnitConstraint(p, unit, "add"));
                break;
            case "groups":
                if (target.groups == null) target.groups = [];
                if (target.groups.find(p => p.groupId == gp.groupId) != null) return false;
                target.groups.push({ groupId: gp.groupId, name: gp.name });
                target.global = false;
                permissions?.forEach(p => mergeGroupConstraint(p, gp, "add"));
                break;
            default:
                target[ct] = selection as boolean;
                target.global = false;
                if (permissions) permissions.forEach(p => mergeConstraints(p, target));
                break;
        }
        return true;
    }
}

export function deleteConstraint(
    target: PermissionConstraintDto,
    ct: ConstraintTypes,
    targetAcceptsEmptyConstraint: boolean,
    selection: ConstraintSelectionType,
    permissions?: RolePermissionDto[]) {
    if (selection != undefined) {
        const ut = selection as PermissionConstraintUnitTypeResponse;
        const unit = selection as PermissionConstraintUnitDto;
        const gp = selection as PermissionConstraintGroupDto;
        switch (ct) {
            case "selfUserUnitTypes":
                target.selfUserUnitTypes = target.selfUserUnitTypes!.filter(p => p.unitTypeId != ut.unitTypeId);
                target.global = targetAcceptsEmptyConstraint ? isEmptyConstraint(target) : isGlobalOnlyConstraint(target);
                permissions?.forEach(p => mergeUnitTypeConstraint(p, ut, "delete"));
                break;
            case "units":
                target.units = target.units!.filter(p => p.unitId != unit.unitId);
                target.global = targetAcceptsEmptyConstraint ? isEmptyConstraint(target) : isGlobalOnlyConstraint(target);
                permissions?.forEach(p => mergeUnitConstraint(p, unit, "delete"));
                break;
            case "groups":
                target.groups = target.groups!.filter(p => p.groupId != gp.groupId);
                target.global = targetAcceptsEmptyConstraint ? isEmptyConstraint(target) : isGlobalOnlyConstraint(target);
                permissions?.forEach(p => mergeGroupConstraint(p, gp, "delete"));
                break;
            default:
                target[ct] = false;
                target.global = targetAcceptsEmptyConstraint ? isEmptyConstraint(target) : isGlobalOnlyConstraint(target);
                if (permissions) permissions.forEach(p => mergeConstraints(p, target));
                break;
        }
    }
}

/**
 * Executa a normalização da Restrição Padrão, convertendo restrições do tipo booleana de False para Null.
 * @param target
 */
export function normalizeDefaultConstraint(target: PermissionConstraintDto) {
    if (target.selfUser === false) target.selfUser = null;
    if (target.selfUserOwnedObjects === false) target.selfUserOwnedObjects = null;
    if (target.allSubordinates === false) target.allSubordinates = null;
    if (target.directSubordinates === false) target.directSubordinates = null;
}

interface RenderConstraintsProps {
    constraints: PermissionConstraintDto;
    isReserved?: boolean;
    onDelete?: DeleteConstraintType;
}

export const RenderConstraints: React.FC<RenderConstraintsProps> = ({ constraints, isReserved = false, onDelete }) => {

    const { t } = useTranslation();

    const names = getConstraintTypeNames(t);

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {constraints.global !== false
                && <span>{isReserved ? <i>{t("Role.Edit.Helpers.GlobalText")}</i> : t("Role.Edit.Helpers.AddConstraintsText")}</span>}

            {constraints.global === false && isEmptyConstraint(constraints)
                && <MprcText color="error">{t("Role.Edit.Helpers.TypeOneConstraintText")}</MprcText>}

            {constraints.directSubordinates === true
                && <MprcChip label={names["directSubordinates"]} onDelete={onDelete ? () => onDelete("directSubordinates", undefined) : undefined} />}

            {constraints.allSubordinates === true
                && <MprcChip label={names["allSubordinates"]} onDelete={onDelete ? () => onDelete("allSubordinates", undefined) : undefined} />}

            {constraints.selfUserOwnedObjects === true
                && <MprcChip label={names["selfUserOwnedObjects"]} onDelete={onDelete ? () => onDelete("selfUserOwnedObjects", undefined) : undefined} />}

            {constraints.selfUser === true
                && <MprcChip label={names["selfUser"]} onDelete={onDelete ? () => onDelete("selfUser", undefined) : undefined} />}

            {constraints.selfUserUnitTypes?.map((item, index) => (
                <MprcChip key={index} label={`${t("Role.Edit.Helpers.SelfUserUnitTypesTextPrefix")}: ${item.name}`} onDelete={onDelete ? () => onDelete("selfUserUnitTypes", item) : undefined} />
            ))}

            {constraints.groups?.map((item, index) => (
                <MprcChip key={index} label={`${names["groups"]}: ${item.name}`} onDelete={onDelete ? () => onDelete("groups", item) : undefined} />
            ))}

            {constraints.units?.map((item, index) => (
                <MprcChip key={index} label={`${item.unitTypeName}: ${item.name}${item.includeDescendants ? "[+]" : ""}`} onDelete={onDelete ? () => onDelete("units", item) : undefined} />
            ))}
        </Box>
    );
};
